import React, { Component } from "react";
import clsx from "clsx";
import { fade, withStyles } from "@material-ui/core/styles";
import AccountCircle from "@material-ui/icons/AccountCircle";
import AddCircle from "@material-ui/icons/AddCircle";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CreateNewFolderIcon from "@material-ui/icons/CreateNewFolder";
import Divider from "@material-ui/core/Divider";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import HomeIcon from "@material-ui/icons/Home";
import IconButton from "@material-ui/core/IconButton";
import InfoIcon from "@material-ui/icons/Info";
import LanguageIcon from "@material-ui/icons/Language";
import MenuIcon from "@material-ui/icons/Menu";
import MoreIcon from "@material-ui/icons/MoreVert";
import SearchIcon from "@material-ui/icons/Search";
import SettingsIcon from "@material-ui/icons/Settings";
import Switch from "@material-ui/core/Switch";
import TextField from "@material-ui/core/TextField";
import Toolbar from "@material-ui/core/Toolbar";
import Tooltip from "@material-ui/core/Tooltip";
import { withRouter } from "react-router-dom";
import { isAdmin } from "../../utility/user-actions";
import { RADIUS_INPUT } from "../../theme/tokens";

import ApiDocsIcon from "../../components/ApiDocsIcon/ApiDocsIcon";
import KodeLogo from "../../components/KodeLogo/KodeLogo";

import AllureDockerMobileMenu from "../AllureDockerMobileMenu/AllureDockerMobileMenu";
import AllureDockerNewFolderDialog from "../AllureDockerNewFolderDialog/AllureDockerNewFolderDialog";
import AllureDockerNewProjectDialog from "../AllureDockerNewProjectDialog/AllureDockerNewProjectDialog";
import AllureDockerConfigDialog from "../AllureDockerConfigDialog/AllureDockerConfigDialog";
import AllureDockerInfoDialog from "../AllureDockerInfoDialog/AllureDockerInfoDialog";
import AllureDockerLanguagesMenu from "../AllureDockerLanguagesMenu/AllureDockerLanguagesMenu";
import AllureDockerSignOutDialog from "../AllureDockerSignOutDialog/AllureDockerSignOutDialog";
import { redirect, redirectRoot } from "../../utility/navigate";
import axios from "../../api/axios-allure-docker";

// Порог инлайновых действий НЕ совпадает с брейкпоинтом панели, и это
// намеренно. Панель ниже md (960) становится temporary, а с 960 —
// persistent шириной 272px, и ровно в этот момент тулбар возвращал себе
// весь набор кнопок. Замерено в браузере: содержимому тулбара нужно 940px
// (без подписи «QA Reports»), то есть с открытой панелью — 1212px вьюпорта.
// До этой правки кнопки просто уезжали за край: на 1024 недоступны были
// settings, sign out, language, about и API docs, включая выход из аккаунта,
// причём ни скролла, ни переполнения в меню не возникало.
//
// Порог задан числом, а не ключом брейкпоинта: ни один из md/lg/xl не
// попадает в измеренную величину, а подгонять под ключ значило бы либо
// оставить обрезку (lg = 1280 < нужного с подписью), либо прятать рабочие
// кнопки на всех ноутбуках (xl = 1920).
const TOOLBAR_INLINE_MIN_WIDTH = 1240;
// Подпись уходит раньше кнопок: она декоративна, а они функциональны. Ровно
// эти 89px и позволяют вернуть действия с 1240, а не с 1360.
const TOOLBAR_WORDMARK_MIN_WIDTH = 1360;

const styles = (theme) => ({
  grow: {
    flexGrow: 1,
  },
  title: {
    display: "none",
    [theme.breakpoints.up("sm")]: {
      display: "block",
    },
  },
  search: {
    position: "relative",
    // Не theme.shape.borderRadius: он теперь 8 для панелей, а поля у KODE — 4.
    borderRadius: RADIUS_INPUT,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: fade(theme.palette.text.primary, 0.06),
    "&:hover": {
      backgroundColor: fade(theme.palette.text.primary, 0.1),
    },
    "&:focus-within": {
      borderColor: theme.palette.primary.main,
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      marginLeft: theme.spacing(3),
      width: "auto",
    },
  },
  searchIcon: {
    padding: theme.spacing(0, 2),
    height: "100%",
    position: "absolute",
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  searchTextField: {
    color: "inherit",
    padding: theme.spacing(0.5),
    paddingLeft: `calc(1em + ${theme.spacing(5)}px)`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("md")]: {
      width: "40ch",
    },
  },
  sectionDesktop: {
    display: "none",
    [theme.breakpoints.up(TOOLBAR_INLINE_MIN_WIDTH)]: {
      display: "flex",
    },
  },
  // Кнопки действий тоже прячем ниже md и уносим в меню ⋮. Раньше они висели
  // в тулбаре всегда, и на 375px он переполнялся на 95px — причём не
  // прокручивался, а обрезался: за краем оставались settings и сама кнопка ⋮,
  // то есть тема, язык, About и API-доки становились недоступны с телефона.
  sectionDesktopActions: {
    display: "none",
    [theme.breakpoints.up(TOOLBAR_INLINE_MIN_WIDTH)]: {
      display: "flex",
      alignItems: "center",
    },
  },
  sectionMobile: {
    display: "flex",
    [theme.breakpoints.up(TOOLBAR_INLINE_MIN_WIDTH)]: {
      display: "none",
    },
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  // Логотип живёт слева и виден от sm: фирменный знак, исчезающий на 1279px,
  // — это баг брендинга, а не адаптивность.
  logo: {
    display: "none",
    alignItems: "center",
    flexShrink: 0,
    marginRight: theme.spacing(1.5),
    [theme.breakpoints.up("sm")]: {
      display: "flex",
    },
  },
  wordmark: {
    display: "none",
    alignItems: "center",
    flexShrink: 0,
    paddingLeft: theme.spacing(1.5),
    borderLeft: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    fontSize: 13,
    fontWeight: 500,
    whiteSpace: "nowrap",
    [theme.breakpoints.up(TOOLBAR_WORDMARK_MIN_WIDTH)]: {
      display: "flex",
    },
  },
  hide: {
    display: "none",
  },
});

class AllureDockerToolbar extends Component {
  state = {
    newProjectDialog: false,
    newFolderDialog: false,
    configDialog: false,
    signOutDialog: false,
    infoDialog: false,
    languagesAnchorEl: null,
    mobileMoreAnchorEl: null,
    searchResults: [],
  };

  openMobileMenu = (event) => {
    this.setState({ mobileMoreAnchorEl: event.currentTarget });
  };

  closeMobileMenu = () => {
    this.setState({ mobileMoreAnchorEl: null });
  };

  openNewProjectDialog = () => {
    this.setState({ newProjectDialog: true });
  };

  closeNewProjectDialog = () => {
    this.setState({ newProjectDialog: false });
  };

  openNewFolderDialog = () => {
    this.setState({ newFolderDialog: true });
  };

  closeNewFolderDialog = () => {
    this.setState({ newFolderDialog: false });
  };

  openConfigDialog = () => {
    this.setState({ configDialog: true });
  };

  closeConfigDialog = () => {
    this.setState({ configDialog: false });
  };

  openSignOutDialog = () => {
    this.setState({ signOutDialog: true });
  };

  closeSignOutDialog = () => {
    this.setState({ signOutDialog: false });
  };

  openInfoDialog = () => {
    this.setState({ infoDialog: true });
  };

  closeInfoDialog = () => {
    this.setState({ infoDialog: false });
  };

  closeLanguagesMenu = () => {
    this.setState({ languagesAnchorEl: null });
  };

  openLanguagesMenu = (event) => {
    this.setState({ languagesAnchorEl: event.currentTarget });
  };

  goToSwagger = () => {
    window.open(`${window._env_.ALLURE_DOCKER_API_URL}/swagger`, "_blank");
  };

  goToHome = () => {
    redirectRoot();
  };

  goToSignIn = () => {
    this.props.history.push(`/signin`);
  }

  handleAPIErrorAlert = (error) => {
    this.props.setAPIAlert(
      "error",
      `Something wrong => ${error.message}`,
      true
    );
  };

  handleSearch = (event, id) => {
    axios
      .get(`/projects/search?id=${id}`)
      .then((response) => {
        const projects = response.data.data.projects;
        const searchResults = [];
        for (const id in projects) {
          searchResults.push(id);
        }
        this.setState({ searchResults: searchResults });
      })
      .catch((error) => {
        redirect(error);
        if (error.status !== 404) {
          this.handleAPIErrorAlert(error);
        }
        this.setState({ searchResults: [] });
      });
  };

  handleSearchValue = (event, value) => {
    if (value) {
      this.props.history.push(`/projects/${value}`);
    }
  };

  render() {
    const { classes } = this.props;

    let isSignInAnOption = "";
    if (this.props.isSignInAnOption) {
      isSignInAnOption = (
        <Tooltip title="Sign in">
          <IconButton color="inherit" aria-label="sign in" onClick={this.goToSignIn}>
            <AccountCircle />
          </IconButton>
        </Tooltip>
      );
    }

    let signOutButton = "";
    if (this.props.isLogoutNeeded) {
      signOutButton = (
        <Tooltip title="Sign out">
          <IconButton color="inherit" aria-label="sign out" onClick={this.openSignOutDialog}>
            <ExitToAppIcon />
          </IconButton>
        </Tooltip>
      );
    }

    return (
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={this.props.handleSideDrawerOpen}
          edge="start"
          className={clsx(
            classes.menuButton,
            this.props.isSideDrawerOpen && classes.hide
          )}
        >
          <MenuIcon />
        </IconButton>

        <div className={classes.logo}>
          <KodeLogo height={16} />
        </div>
        <div className={classes.wordmark}>QA Reports</div>

        <div className={classes.search}>
          <div className={classes.searchIcon}>
            <SearchIcon />
          </div>
          <Autocomplete
            className={classes.searchTextField}
            options={this.state.searchResults}
            onInputChange={this.handleSearch}
            onChange={this.handleSearchValue}
            noOptionsText={'project not found'}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Project ID..."
                margin="none"
                variant="standard"
                InputProps={{ ...params.InputProps, disableUnderline: true }}
              />
            )}
          />
        </div>

        <div className={classes.grow} />

        <div className={classes.sectionDesktopActions}>
          <Tooltip title="Home">
            <IconButton color="inherit" aria-label="home" onClick={this.goToHome}>
              <HomeIcon />
            </IconButton>
          </Tooltip>

          {isSignInAnOption}

          {/* Без Tooltip: кнопка бывает disabled, а MUI v4 Tooltip на
              disabled-потомке пишет console.error на каждый рендер. */}
          <IconButton
            color="inherit"
            aria-label="new project"
            onClick={this.openNewProjectDialog}
            disabled={!isAdmin()}
          >
            <AddCircle />
          </IconButton>

          <IconButton
            color="inherit"
            aria-label="create folder"
            onClick={this.openNewFolderDialog}
            disabled={!isAdmin()}
          >
            <CreateNewFolderIcon />
          </IconButton>

          <Tooltip title="Settings">
            <IconButton color="inherit" aria-label="settings" onClick={this.openConfigDialog}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>

          {signOutButton}
          <Divider orientation="vertical" flexItem />
        </div>

        <div className={classes.sectionDesktop}>
          {/* Switch внутри IconButton — интерактивное в интерактивном:
              невалидный HTML и двойной ripple. */}
          <Tooltip title={this.props.darkState ? "Light mode" : "Dark mode"}>
            <Switch
              checked={this.props.darkState}
              onChange={this.props.handleThemeChange}
              inputProps={{
                "aria-label": this.props.darkState ? "switch to light mode" : "switch to dark mode",
              }}
            />
          </Tooltip>

          <Tooltip title="Language">
            <IconButton color="inherit" aria-label="language" onClick={this.openLanguagesMenu}>
              <LanguageIcon />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem />

          <Tooltip title="About">
            <IconButton color="inherit" aria-label="about" onClick={this.openInfoDialog}>
              <InfoIcon />
            </IconButton>
          </Tooltip>

          <IconButton color="inherit" aria-label="API docs" onClick={this.goToSwagger}>
            <ApiDocsIcon />
          </IconButton>
        </div>

        <div className={classes.sectionMobile}>
          <IconButton
            aria-label="show more"
            aria-controls="primary-search-account-menu-mobile"
            aria-haspopup="true"
            onClick={this.openMobileMenu}
            color="inherit"
          >
            <MoreIcon />
          </IconButton>
        </div>

        {/* Меню живёт здесь, а не в AllureDockerBar: все обработчики и диалоги
            принадлежат тулбару, и прокидывать их через два уровня незачем. */}
        <AllureDockerMobileMenu
          isMobileMenuOpen={Boolean(this.state.mobileMoreAnchorEl)}
          anchorEl={this.state.mobileMoreAnchorEl}
          handleMobileMenuClose={this.closeMobileMenu}
          darkState={this.props.darkState}
          handleThemeChange={this.props.handleThemeChange}
          goToHome={this.goToHome}
          goToSignIn={this.goToSignIn}
          openNewProjectDialog={this.openNewProjectDialog}
          openNewFolderDialog={this.openNewFolderDialog}
          openConfigDialog={this.openConfigDialog}
          openSignOutDialog={this.openSignOutDialog}
          isLogoutNeeded={this.props.isLogoutNeeded}
          isSignInAnOption={this.props.isSignInAnOption}
          setAPIAlert={this.props.setAPIAlert}
        />

        {/* Диалоги — портальные, поэтому лежали внутри sectionMobile
            (display:none на десктопе) и всё равно работали. Вынесены наружу,
            чтобы это не выглядело случайностью. */}
        <React.Fragment>
          <AllureDockerNewProjectDialog
            setAPIAlert={this.props.setAPIAlert}
            open={this.state.newProjectDialog}
            closeNewProjectDialog={this.closeNewProjectDialog}
            getProjects={this.props.getProjects}
          />
          <AllureDockerNewFolderDialog
            open={this.state.newFolderDialog}
            closeNewFolderDialog={this.closeNewFolderDialog}
            createFolder={this.props.createFolder}
          />
          <AllureDockerConfigDialog
            setAPIAlert={this.props.setAPIAlert}
            open={this.state.configDialog}
            closeConfigDialog={this.closeConfigDialog}
          />
          <AllureDockerSignOutDialog
            setAPIAlert={this.props.setAPIAlert}
            open={this.state.signOutDialog}
            closeSignOutDialog={this.closeSignOutDialog}
          />
          <AllureDockerInfoDialog
            open={this.state.infoDialog}
            handleCloseDialog={this.closeInfoDialog}
          />
          <AllureDockerLanguagesMenu
            setAPIAlert={this.props.setAPIAlert}
            closeLanguagesMenu={this.closeLanguagesMenu}
            anchorEl={this.state.languagesAnchorEl}
          />
        </React.Fragment>
      </Toolbar>
    );
  }
}

export default withStyles(styles, { withTheme: true })(
  withRouter(AllureDockerToolbar)
);
